import { Link } from '@tanstack/react-router';
import { format, formatDistanceToNowStrict } from 'date-fns';
import { type KeyboardEvent } from 'react';

import { Badge } from '@apps/web/components/ui/badge';
import { Button } from '@apps/web/components/ui/button';
import { Spinner } from '@apps/web/components/ui/spinner';
import {
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableHeader,
    TableRow,
} from '@apps/web/components/ui/table';
import { formatDelimitedLabel } from '@apps/web/lib/format_utils';
import { cn } from '@apps/web/lib/utils';
import {
    type AgentTraceRunCursorRead,
    type AgentTraceRunListRow,
} from '@libraries/typescript/api_client';

type RunStatus = AgentTraceRunListRow['status'];

const RUN_STATUS_CLASS_NAMES: Record<RunStatus, string> = {
    idle: 'bg-muted text-foreground/80',
    running: 'bg-blue-100 text-blue-800 dark:bg-blue-950/50 dark:text-blue-300',
    failed: 'bg-red-100 text-red-800 dark:bg-red-950/50 dark:text-red-300',
    completed:
        'bg-green-100 text-green-800 dark:bg-green-950/50 dark:text-green-300',
    cancelled:
        'bg-amber-100 text-amber-800 dark:bg-amber-950/50 dark:text-amber-300',
};

export function EphemeralRunTable({
    runs,
    nextCursor,
    isLoadingMore,
    onLoadMore,
    onSelectRun,
}: {
    readonly runs: Array<AgentTraceRunListRow>;
    readonly nextCursor: AgentTraceRunCursorRead | null;
    readonly isLoadingMore: boolean;
    readonly onLoadMore: (cursor: AgentTraceRunCursorRead) => void;
    readonly onSelectRun: (runId: string) => void;
}) {
    function handleRowKeyDown(
        event: KeyboardEvent<HTMLTableRowElement>,
        runId: string,
    ) {
        if (event.target === event.currentTarget && event.key === 'Enter') {
            onSelectRun(runId);
        }
    }

    return (
        <div className="flex flex-col gap-3">
            <div className="overflow-x-auto rounded-md border">
                <Table>
                    <TableHeader>
                        <TableRow>
                            <TableHead>Title / Run ID</TableHead>
                            <TableHead>Organization</TableHead>
                            <TableHead>Run Type</TableHead>
                            <TableHead>Creator</TableHead>
                            <TableHead>Client Surface</TableHead>
                            <TableHead>Status</TableHead>
                            <TableHead>Created</TableHead>
                            <TableHead>Updated</TableHead>
                            <TableHead>Last Activity</TableHead>
                        </TableRow>
                    </TableHeader>
                    <TableBody>
                        {runs.map((run) => {
                            const organizationInternalName =
                                run.organization_internal_name;
                            const creatorPrimary =
                                run.created_by_user_name ??
                                run.created_by_user_email ??
                                run.created_by_user_id ??
                                '—';
                            const creatorSecondary = run.created_by_user_name
                                ? (run.created_by_user_email ??
                                  run.created_by_user_id)
                                : run.created_by_user_email
                                  ? run.created_by_user_id
                                  : null;
                            return (
                                <TableRow
                                    key={run.run_id}
                                    tabIndex={0}
                                    className="hover:bg-muted/50 cursor-pointer"
                                    onClick={() => onSelectRun(run.run_id)}
                                    onKeyDown={(event) =>
                                        handleRowKeyDown(event, run.run_id)
                                    }
                                >
                                    <TableCell className="max-w-80 min-w-56">
                                        <Link
                                            to="/admin/agent-traces/run/$runId"
                                            params={{ runId: run.run_id }}
                                            className="block min-w-0"
                                            onClick={(event) =>
                                                event.stopPropagation()
                                            }
                                        >
                                            <div className="truncate font-medium">
                                                {run.title ?? 'Untitled run'}
                                            </div>
                                            <div className="text-muted-foreground mt-1 truncate font-mono text-xs">
                                                {run.run_id}
                                            </div>
                                        </Link>
                                    </TableCell>
                                    <TableCell className="max-w-52 text-sm">
                                        <div className="truncate">
                                            {run.organization_name ?? '—'}
                                        </div>
                                        {organizationInternalName ? (
                                            <div className="text-muted-foreground mt-1 truncate text-xs">
                                                {organizationInternalName}
                                            </div>
                                        ) : null}
                                    </TableCell>
                                    <TableCell className="text-sm whitespace-nowrap">
                                        {formatDelimitedLabel(run.run_type)}
                                    </TableCell>
                                    <TableCell className="max-w-52 text-sm">
                                        <div className="truncate">
                                            {creatorPrimary}
                                        </div>
                                        {creatorSecondary ? (
                                            <div className="text-muted-foreground mt-1 truncate text-xs">
                                                {creatorSecondary}
                                            </div>
                                        ) : null}
                                    </TableCell>
                                    <TableCell className="text-sm whitespace-nowrap">
                                        {run.client_surface
                                            ? formatDelimitedLabel(
                                                  run.client_surface,
                                              )
                                            : '—'}
                                    </TableCell>
                                    <TableCell className="whitespace-nowrap">
                                        <Badge
                                            variant="secondary"
                                            className={cn(
                                                'text-xs',
                                                RUN_STATUS_CLASS_NAMES[
                                                    run.status
                                                ],
                                            )}
                                        >
                                            {formatDelimitedLabel(run.status)}
                                        </Badge>
                                    </TableCell>
                                    <TableCell className="text-muted-foreground text-sm whitespace-nowrap">
                                        <time dateTime={run.created_at}>
                                            {format(
                                                new Date(run.created_at),
                                                'MMM d, h:mm a',
                                            )}
                                        </time>
                                    </TableCell>
                                    <TableCell className="text-muted-foreground text-sm whitespace-nowrap">
                                        <time dateTime={run.updated_at}>
                                            {format(
                                                new Date(run.updated_at),
                                                'MMM d, h:mm a',
                                            )}
                                        </time>
                                    </TableCell>
                                    <TableCell className="text-muted-foreground text-sm whitespace-nowrap">
                                        <time dateTime={run.last_activity_at}>
                                            {formatDistanceToNowStrict(
                                                new Date(run.last_activity_at),
                                                { addSuffix: true },
                                            )}
                                        </time>
                                    </TableCell>
                                </TableRow>
                            );
                        })}
                    </TableBody>
                </Table>
            </div>
            {nextCursor ? (
                <div className="flex justify-center">
                    <Button
                        variant="outline"
                        disabled={isLoadingMore}
                        onClick={() => onLoadMore(nextCursor)}
                    >
                        {isLoadingMore ? (
                            <Spinner size="sm" tone="muted" />
                        ) : null}
                        {isLoadingMore ? 'Loading more…' : 'Load More'}
                    </Button>
                </div>
            ) : null}
        </div>
    );
}
